import { useCallback, useEffect, useId, useImperativeHandle, useRef, type ClipboardEvent, type KeyboardEvent, type Ref } from 'react';
import { cn } from '@/shared/lib/cn';

export interface OtpInputHandle {
  /** Devolve o foco à primeira caixa. Para quem esvazia o campo de fora. */
  focusFirst: () => void;
}

interface OtpInputProps {
  length: number;
  value: string;
  onChange: (value: string) => void;
  /** Disparado quando o último dígito é preenchido — permite submeter sem clique. */
  onComplete?: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  label: string;
  ref?: Ref<OtpInputHandle>;
}

const DIGITS_ONLY = /\D/g;

/**
 * Campo de código de 6 dígitos.
 *
 * Um `<input>` por dígito é o padrão que os usuários esperam de um OTP, mas
 * exige cuidado: colar o código inteiro, navegar com setas, apagar voltando e
 * autofill do SMS/e-mail. Tudo tratado aqui para que a tela de login não saiba
 * de índice de foco.
 */
export function OtpInput({ length, value, onChange, onComplete, disabled = false, invalid = false, label, ref }: OtpInputProps) {
  const groupId = useId();
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const digits = Array.from({ length }, (_, index) => value[index] ?? '');
  const isComplete = value.length === length;

  // Só na transição de incompleto para completo, e não a cada render com o campo
  // cheio: `onComplete` é recriado pelo pai a cada re-render (ele depende de
  // `submitting`), e disparar por identidade nova fazia o código recusado ser
  // reenviado sozinho — a resposta de erro re-renderizava, o efeito rodava outra vez
  // com os mesmos seis dígitos e o login entrava em laço, com as caixas desabilitadas
  // no meio de cada tentativa e o operador sem conseguir digitar o código certo.
  //
  // Também é o que impede submeter no meio de uma correção: sobrescrever um dígito de
  // um código cheio mantém o comprimento em seis, e por dígito trocado sairia uma
  // tentativa com o código ainda pela metade.
  const wasComplete = useRef(false);
  useEffect(() => {
    if (isComplete && !wasComplete.current) onComplete?.(value);
    wasComplete.current = isComplete;
  }, [isComplete, value, onComplete]);

  const focusAt = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(length - 1, index));
      inputsRef.current[clamped]?.focus();
      inputsRef.current[clamped]?.select();
    },
    [length],
  );

  useImperativeHandle(ref, () => ({ focusFirst: () => focusAt(0) }), [focusAt]);

  const writeAt = (index: number, raw: string) => {
    const cleaned = raw.replace(DIGITS_ONLY, '');
    if (!cleaned) return;

    // Digitar/colar vários dígitos preenche a partir da posição atual — nunca depois
    // do fim do valor: com o campo esvaziado de fora o foco pode estar numa caixa à
    // frente, e escrever ali abriria um buraco no meio do código.
    const start = Math.min(index, value.length);
    const next = (value.slice(0, start) + cleaned + value.slice(start + cleaned.length)).slice(0, length);
    onChange(next);
    focusAt(start + cleaned.length);
  };

  const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (digits[index]) {
        onChange(value.slice(0, index) + value.slice(index + 1));
      } else if (index > 0) {
        onChange(value.slice(0, index - 1) + value.slice(index));
        focusAt(index - 1);
      }
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusAt(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusAt(index + 1);
    }
  };

  const handlePaste = (index: number) => (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    writeAt(index, event.clipboardData.getData('text'));
  };

  return (
    <div role="group" aria-labelledby={`${groupId}-label`} className="flex flex-col gap-1.5">
      {/* Dentro do cartão do login, que é pintado com a marca: o rótulo segue os
          outros textos de lá, e não o cinza do painel. */}
      <span id={`${groupId}-label`} className="text-[0.8125rem] font-medium text-on-brand/75">
        {label}
      </span>

      <div className="flex gap-2">
        {digits.map((digit, index) => (
          <input
            // A posição É a identidade de cada caixa; não há reordenação possível.
            key={index}
            ref={element => {
              inputsRef.current[index] = element;
            }}
            value={digit}
            onChange={event => writeAt(index, event.target.value)}
            onKeyDown={handleKeyDown(index)}
            onPaste={handlePaste(index)}
            onFocus={event => event.target.select()}
            disabled={disabled}
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={length}
            aria-label={`Dígito ${index + 1} de ${length}`}
            aria-invalid={invalid ? true : undefined}
            className={cn(
              // As caixas são brancas, e não da cor do cartão: o dígito usa
              // `brand-ink`, que é a marca já escurecida para ser legível sobre claro.
              'tabular h-12 w-full min-w-0 rounded-lg border bg-surface text-center text-lg font-semibold text-brand-ink',
              'transition-shadow duration-150 focus:outline-none focus:shadow-focus disabled:bg-surface-muted disabled:text-brand-ink/50',
              invalid ? 'border-rejected' : 'border-line-strong focus:border-brand-500',
            )}
          />
        ))}
      </div>
    </div>
  );
}
